import React, { useDebugValue, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  GetProjectListAction,
  uploadDocuments,
} from "store/actions/ProjectActions";
import { getProfileAction } from "store/actions/UserActions";
import formValidation from "utils/validations";
import { showFormError, showFormErrors } from "utils/CommonFunctions";
import { formatDateToMMDDYYYY, handleFormatDate } from "utils/common";
import { VariableKeys } from "utils/VariableKeys";
import {
  createContractAction,
  GetContractDetailsActon,
  shareContractAction,
  signContractAction,
  updateContractAction,
  updateDocumentAction,
} from "store/actions/ContractActions";
import SignatureModal from "../../commonComponents/DigitalSign";
import {
  initialArtworkData,
  initialMusicVideoData,
  initialProducerData,
  initialPublishingData,
  initialRemixData,
  initialSideArtistData,
  initialSignState,
} from "./initialDataforForms";
import { set } from "store";
import { agreementsList } from "shared/components/AllDropdownOptions";
import { showToastAction } from "store/slices/commonSlice";

const ContractContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [projectsList, setProjectsList] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [signatureModalVisible, setSignatureModalVisible] = useState(false);
  const [typedSignature, setTypedSignature] = useState("");
  const [drawnSignature, setDrawnSignature] = useState("");
  const [currentSigner, setCurrentSigner] = useState(null);
  const [sign, setSign] = useState({});
  const [documentPayload, setDocumentPayload] = useState(null);
  const [producerData, setProducerData] = useState(initialProducerData);
  const [sideArtistData, setSideArtistData] = useState(initialSideArtistData);
  const [publishingData, setPublishingData] = useState(initialPublishingData);
  const [musicVideoData, setMusicVideoData] = useState(initialMusicVideoData);
  const [artworkData, setArtworkData] = useState(initialArtworkData);
  const [remixData, setRemixData] = useState(initialRemixData);
  const [contractData, setContractData] = useState(null);
  const [shareDialog, setShareDialog] = useState(false);
  const [emails, setEmails] = useState({ email1: "", email2: "" });
  const [showAdditionalEmail, setShowAdditionalEmail] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [contractStatus, setContractStatus] = useState(null);
  const [signData, setSignData] = useState({
    signature: "",
    typedSignature: "",
  });
  const [data, setData] = useState({
    agreementId: "",
    project: "",
    agreementType: "",
    agreementDate: handleFormatDate(new Date(), VariableKeys.FULL_MONTH),
  });
  const { state } = useLocation();
  const [htmlContent, setHtmlContent] = useState("");
  const quillRef = useRef(null);

  React.useEffect(() => {
    if (state?.activeTab) {
      setActiveIndex(state.activeTab);
    }
  }, [state]);

  useEffect(() => {
    if (data.project) {
      const selectedProject = projectsList.find(
        (proj) => proj.value === data.project
      );

      if (selectedProject && selectedProject.agreements) {
        const formattedAgreements = selectedProject.agreements
          .filter(
            (agreement) =>
              agreement.status === "NOT_STARTED" &&
              agreement.documentName.toLowerCase() !== "sample agreement"
          )
          .map((agreement) => ({
            agreementId: agreement._id,
            name: agreement.documentName,
            value: agreement.documentName,
            status: agreement.status,
          }));

        setAgreements(formattedAgreements);
      } else {
        setAgreements([]);
      }
    }
  }, [data.project, projectsList]);


  const addEmailField = () => {
    if (!showAdditionalEmail) {
      setShowAdditionalEmail(true);
    }
  };

  const removeEmailField = () => {
    setShowAdditionalEmail(false);
    setEmails((prev) => ({ ...prev, email2: "" }));
  };

  const handleEmailChange = ({ name, value }) => {
    const formErrors = formValidation(name, value, data);
    setEmails((prev) => ({ ...prev, [name]: value, formErrors }));
  };

  const handleShareContract = () => {
    const payload = {
      contractId: id,
      email: emails.email1,
      ...(showAdditionalEmail && emails.email2 && { email: emails.email2 }),
    };
    const formData = new FormData();
    formData.append("contractId", id);
    formData.append("email", emails.email1);
    if (showAdditionalEmail && emails.email2) {
      formData.append("email", emails.email2);
    }
    dispatch(
      shareContractAction(payload, setShareLoading, dispatch, (res) => {
        setShareDialog(false);
        setEmails({ email1: "", email2: "" });
        setShowAdditionalEmail(false);
      })
    );
  };

  const { profile } = useSelector((state) => state.user);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    dispatch(
      GetContractDetailsActon(id, (data) => {
        console.log(data, "res");
        setContractStatus(data?.payload?.status);
        setData({
          agreementType: data?.payload?.agreementType,
          agreementDate: data?.payload?.agreementDate
            ? new Date(data.payload.agreementDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "2-digit",
              })
            : "",
          project: data?.payload?.project,
        });
        setRemixData({
          agreementName: data?.payload?.agreementName,
          fullLegalName: data?.payload?.primaryArtist?.fullLegalName,
          name: data?.payload?.primaryArtist?.name,
          address: data?.payload?.primaryArtist?.address,
          remixerFullLegalName: data?.payload?.details?.remixerFullLegalName,
          remixerName: data?.payload?.details?.remixerName,
          remixerAddress: data?.payload?.details?.remixerAddress,
          releaseName: data?.payload?.details?.releaseName,
          editNumber: data?.payload?.details?.editNumber,
          bearingTheCost: data?.payload?.details?.bearingTheCost,
          remixAudioFormat: data?.payload?.details?.remixAudioFormat,
          remixFee: data?.payload?.details?.remixFee,
          splitPercentage: data?.payload?.details?.splitPercentage,
          remixCredit: data?.payload?.details?.remixCredit,
          deadline: formatDateToMMDDYYYY(data?.payload?.details?.deadline),
          clauses: data?.payload?.clauses || [],
          notice: {
            email:
              data?.payload?.clauses?.find((c) => c.name === "notice")?.value ||
              "",
          },
          remedies: {
            law:
              data?.payload?.clauses?.find((c) => c.name === "remedies")
                ?.value || "",
          },
        });
        setArtworkData({
          agreementName: data?.payload?.agreementName,
          fullLegalName: data?.payload?.primaryArtist?.fullLegalName,
          name: data?.payload?.primaryArtist?.name,
          address: data?.payload?.primaryArtist?.address,
          artworkCreatorName: data?.payload?.details?.artworkCreatorName,
          artworkCreatorAddress: data?.payload?.details?.artworkCreatorAddress,
          musicRelaseName: data?.payload?.details?.musicRelaseName,
          artWorkDescription: data?.payload?.details?.artWorkDescription,
          artworkFee: data?.payload?.details?.artworkFee,
          paymentStructure: data?.payload?.details?.paymentStructure,
          deliveryFormat: data?.payload?.details?.deliveryFormat,
          deliveryDate: formatDateToMMDDYYYY(
            data?.payload?.details?.deliveryDate
          ),
          revisonRound: data?.payload?.details?.revisonRound,
          additionalCharges: data?.payload?.details?.additionalCharges,
          days: data?.payload?.details?.days,
          law: data?.payload?.details?.law,
          warrenties: data?.payload?.details?.warrenties,
          currencySymbol: data?.payload?.details?.symbol,
        });
        setMusicVideoData({
          agreementName: data?.payload?.agreementName,
          fullLegalName: data?.payload?.primaryArtist?.fullLegalName,
          name: data?.payload?.primaryArtist?.name,
          address: data?.payload?.primaryArtist?.address,
          director: data?.payload?.details?.director,
          bussinessAddress: data?.payload?.details?.bussinessAddress,
          realeaseTitle: data?.payload?.details?.realeaseTitle,
          scopeProduction: data?.payload?.details?.scopeProduction,
          musicVideoFee: data?.payload?.details?.musicVideoFee,
          paymentSchedule: data?.payload?.details?.paymentSchedule,
          paymentTerms: data?.payload?.details?.paymentTerms,
          videoFormat: data?.payload?.details?.videoFormat,
          deliveryDate: formatDateToMMDDYYYY(
            data?.payload?.details?.deliveryDate
          ),
          days: data?.payload?.details?.days,
          disputeSettlement: data?.payload?.details?.disputeSettlement,
          jurisdiction: data?.payload?.details?.jurisdiction,
          clauses: data?.payload?.clauses || [],
          day: data?.payload?.details?.termination?.day,
          law: data?.payload?.details?.governmentlaw?.law,
          producerQuotation: data?.payload?.details?.producerQuotation,
        });
        setPublishingData({
          agreementName: data?.payload?.agreementName,
          fullLegalName: data?.payload?.primaryArtist?.fullLegalName,
          name: data?.payload?.primaryArtist?.name,
          address: data?.payload?.primaryArtist?.address,
          coWriterFullLegalName: data?.payload?.details?.coWriterFullLegalName,
          coWriterName: data?.payload?.details?.coWriterName,
          coWriterAddress: data?.payload?.details?.coWriterAddress,
          coWriterFullLegalName2:
            data?.payload?.details?.coWriterFullLegalName2,
          coWriterName2: data?.payload?.details?.coWriterName2,
          coWriterAddress2: data?.payload?.details?.coWriterAddress2,
          publishingSplit: data?.payload?.details?.publishingSplit,
          controlledComposition: data?.payload?.details?.controlledComposition,
        });
        setSideArtistData({
          agreementName: data?.payload?.agreementName,
          fullLegalName: data?.payload?.primaryArtist?.fullLegalName,
          name: data?.payload?.primaryArtist?.name,
          address: data?.payload?.primaryArtist?.address,
          sideArtistFullLegalName:
            data?.payload?.details?.sideArtistFullLegalName,
          sideArtistName: data?.payload?.details?.sideArtistName,
          sideArtistAddress: data?.payload?.details?.sideArtistAddress,
          services: data?.payload?.details?.services,
          otherServices: data?.payload?.details?.otherServices,
          project: data?.payload?.details?.project,
          feeType: data?.payload?.details?.feeType,
          amount: data?.payload?.details?.amount,
          credit: data?.payload?.details?.credit,
          royalty: data?.payload?.details?.royalty,
          royaltyPaymentTimeline:
            data?.payload?.details?.royaltyPaymentTimeline,
          royaltyThreshold: data?.payload?.details?.royaltyThreshold,
          noticePeriod: data?.payload?.details?.noticePeriod,
          approvalTime: data?.payload?.details?.approvalTime,
          labelWavier: data?.payload?.details?.labelWavier,
          indemnification: data?.payload?.details?.indemnification,
          notice: data?.payload?.details?.notice,
          terminations: data?.payload?.details?.terminations,
          law: data?.payload?.details?.law,
          currencySymbol: data?.payload?.details?.currencySymbol,
          effectiveDate: data?.payload?.details?.effectiveDate,
          labelName: data?.payload?.details?.labelName,
          labelAddress: data?.payload?.details?.labelAddress,
          labelSideArtistName: data?.payload?.details?.labelSideArtistName,
          labelSideArtistAddress:
            data?.payload?.details?.labelSideArtistAddress,
          labelPrimaryArtistName:
            data?.payload?.details?.labelPrimaryArtistName,
          grantRights: data?.payload?.details?.grantRights,
          additionalObligations: data?.payload?.details?.additionalObligations,
          rights: data?.payload?.details?.rights,
          representations: data?.payload?.details?.representations,
          jurisdiction: data?.payload?.details?.jurisdiction,
          clauses: data?.payload?.clauses || [],
        });
        setProducerData({
          agreementName: data?.payload?.agreementName,
          fullLegalName: data?.payload?.primaryArtist?.fullLegalName,
          name: data?.payload?.primaryArtist?.name,
          address: data?.payload?.primaryArtist?.address,
          producerName: data?.payload?.details?.producerName,
          producerAddress: data?.payload?.details?.producerAddress,
          services: data?.payload?.details?.services,
          trackName: data?.payload?.details?.trackName,
          approvalProcess: data?.payload?.details?.approvalProcess,
          deliveryMethod: data?.payload?.details?.deliveryMethod,
          workflow: data?.payload?.details?.workflow,
          deadline: formatDateToMMDDYYYY(data?.payload?.details?.deadline),
          credit: data?.payload?.details?.credit,
          fee: data?.payload?.details?.fee,
          masterRoyalty: data?.payload?.details?.masterRoyalty,
          recoupabilityClause: data?.payload?.details?.recoupabilityClause,
          accountingType: data?.payload?.details?.accountingType,
          accountingCost: data?.payload?.details?.accountingCost,
          accountingDispute: data?.payload?.details?.accountingDispute,
          jurisdiction: data?.payload?.details?.remedies?.jurisdiction,
          procedure: data?.payload?.details?.remedies?.procedure,
          preagreedCompensation:
            data?.payload?.details?.remedies?.preagreedCompensation,
          clauses: data?.payload?.clauses || [],
        });
        setLoading(false);
      })
    );
  }, [id, dispatch]);

  useEffect(() => {
    if (!profile?._id) {
      dispatch(getProfileAction(dispatch));
    }
  }, [profile, dispatch]);

  useEffect(() => {
    const fetchProjects = async () => {
      const action = GetProjectListAction(
        10,
        1,
        "",
        () => {},
        dispatch,
        (result) => {
          if (result && result.list) {
            const projects = result.list.map((item) => ({
              name: item.projectName,
              value: item._id,
              agreements: item.agreements || [],
            }));
            setProjectsList(projects);
          }
        }
      );
      await action();
    };

    fetchProjects();
  }, []);

  const handleChange = ({ name, value }) => {
    const formErrors = formValidation(name, value, data);
    if (name === "agreementType") {
      const selectedAgreement = agreements.find((item) => item.name === value);
      setData((prev) => ({ 
        ...prev, 
        [name]: value, 
        ["agreementId"]: selectedAgreement?.agreementId || "" , 
        formErrors }));
    } else {
      setData((prev) => ({ ...prev, [name]: value, formErrors }));
    }
  };

  const onTermsSubmit = () => {
    if (data?.agreementType === "Producer Agreement") {
      onProducerSubmit();
    } else if (data?.agreementType === "Side Artist Agreement") {
      onSideArtistSubmit();
    } else if (data?.agreementType === "Publishing Split Form") {
      onPublishingSubmit();
    } else if (data?.agreementType === "Music Video Agreement") {
      onMusicVideoSubmit();
    } else if (data?.agreementType === "Artwork Agreement") {
      onArtworkSubmit();
    } else if (data?.agreementType === "Remix Agreement") {
      onRemixSubmit();
    }
  };

  const onProducerSubmit = async () => {
    let ignoreInputs = [];
    if (!data?.accountingType || data?.accountingType === "na") {
      ignoreInputs = ["accountingCost", "accountingDispute"];
    }
    const hasFormErrors = showFormError(
      producerData,
      setProducerData,
      ignoreInputs
    );

    // If no form errors and no remedies error, proceed with API call
    if (!hasFormErrors) {
      // if (producerData?.document && typeof producerData.document === "object") {
      //   await dispatch(
      //     uploadDocuments(producerData.document, dispatch, (imgRes) => {
      //       producerData.document = imgRes?.path;
      //     })
      //   );
      // }

      const payload = {
        agreementName: producerData.agreementName,
        primaryArtist: {
          fullLegalName: producerData.fullLegalName,
          name: producerData.name,
          address: producerData.address,
        },
        details: {
          producerName: producerData.producerName,
          producerAddress: producerData.producerAddress,
          services: producerData.services,
          trackName: producerData.trackName,
          approvalProcess: producerData.approvalProcess,
          deliveryMethod: producerData.deliveryMethod,
          deadline: producerData.deadline,
          fee: producerData.fee,
          masterRoyalty: producerData.masterRoyalty,
          workflow: producerData.workflow,
          recoupabilityClause: producerData.recoupabilityClause,
          credit: producerData.credit,
          accountingType: producerData.accountingType,
          accountingCost: producerData.accountingCost,
          accountingDispute: producerData.accountingDispute,
          remedies: producerData.remedies,
        },
        agreementId: data.agreementId,
        clauses: producerData.clauses,
        agreementType: data.agreementType,
        project: data.project,
        agreementDate: handleFormatDate(new Date()),
      };

      if (id) {
        dispatch(
          updateContractAction(id, payload, setLoading, (res) => {
            if (res?.success) {
              setProducerData(initialProducerData);
            }
          })
        );
      } else {
        dispatch(
          createContractAction(payload, setLoading, dispatch, (res) => {
            if (res.success) {
              // Reset the form fields after successful submission
              setProducerData(initialProducerData);
            }
          })
        );
      }
    }
  };

  const onSideArtistSubmit = async () => {
    const hasFormErrors = showFormError(sideArtistData, setSideArtistData);
    let remediesError = {};
    const remediesClause = (data?.clauses || []).find(
      (item) => item.name === "remedies"
    );
    if (remediesClause) {
      remediesError = formValidation(
        "remedies",
        remediesClause.value || "",
        data
      );
    }
    if (remediesError?.remedies) {
      setData((prev) => ({
        ...prev,
        formErrors: { ...prev.formErrors, ...remediesError },
      }));
    }
    if (!hasFormErrors && !remediesError?.remedies) {
      const payload = {
        agreementName: sideArtistData.agreementName,
        primaryArtist: {
          fullLegalName: sideArtistData.fullLegalName,
          name: sideArtistData.name,
          address: sideArtistData.address,
        },
        details: {
          sideArtistFullLegalName: sideArtistData.sideArtistFullLegalName,
          sideArtistName: sideArtistData.sideArtistName,
          sideArtistAddress: sideArtistData.sideArtistAddress,
          services: sideArtistData.services,
          feeType: sideArtistData.feeType,
          amount: sideArtistData.amount,
          credit: sideArtistData.credit,
          approvalTime: sideArtistData.approvalTime,
          royalty: sideArtistData.royalty,
          royaltyPaymentTimeline: sideArtistData.royaltyPaymentTimeline,
          royaltyThreshold: sideArtistData.royaltyThreshold,
          noticePeriod: sideArtistData.noticePeriod,
          approvalTime: sideArtistData.approvalTime,
          labelWavier: sideArtistData.labelWavier,
          indemnification: sideArtistData.indemnification,
          notice: sideArtistData.notice,
          terminations: sideArtistData.terminations,
          law: sideArtistData.law,
          currencySymbol: sideArtistData.currencySymbol,
          effectiveDate: sideArtistData.effectiveDate,
          labelName: sideArtistData.labelName,
          labelAddress: sideArtistData.labelAddress,
          labelSideArtistName: sideArtistData.labelSideArtistName,
          labelSideArtistAddress: sideArtistData.labelSideArtistAddress,
          labelPrimaryArtistName: sideArtistData.labelPrimaryArtistName,
          grantRights: sideArtistData.grantRights,
          additionalObligations: sideArtistData.additionalObligations,
          rights: sideArtistData.rights,
          representations: sideArtistData.representations,
          jurisdiction: sideArtistData.jurisdiction,
          otherServices: sideArtistData.otherServices,
          project: sideArtistData.project,
        },
        clauses: sideArtistData.clauses,
        agreementType: data.agreementType,
        agreementId: data.agreementId,
        agreementDate: handleFormatDate(new Date()),
        project: data.project,
      };
      if (id) {
        dispatch(
          updateContractAction(id, payload, setLoading, (res) => {
            if (res?.success) {
              setSideArtistData(initialSideArtistData);
            }
          })
        );
      } else {
        dispatch(
          createContractAction(payload, setLoading, dispatch, (res) => {
            if (res.success) {
              setSideArtistData(initialSideArtistData);
            }
          })
        );
      }
    }
  };

  const onPublishingSubmit = async () => {
    const hasFormErrors = showFormError(publishingData, setPublishingData);
    if (!hasFormErrors) {
      const payload = {
        agreementName: publishingData.agreementName,
        primaryArtist: {
          fullLegalName: publishingData.fullLegalName,
          name: publishingData.name,
          address: publishingData.address,
        },
        details: {
          coWriterFullLegalName: publishingData.coWriterFullLegalName,
          coWriterName: publishingData.coWriterName,
          coWriterAddress: publishingData.coWriterAddress,
          coWriterFullLegalName2: publishingData.coWriterFullLegalName2,
          coWriterName2: publishingData.coWriterName2,
          coWriterAddress2: publishingData.coWriterAddress2,
          publishingSplit: publishingData.publishingSplit,
          controlledComposition: publishingData.controlledComposition,
        },
        agreementType: data.agreementType,
        agreementId: data.agreementId,
        agreementDate: handleFormatDate(new Date()),
        project: data.project,
      };
      if (id) {
        dispatch(
          updateContractAction(id, payload, setLoading, (res) => {
            if (res?.success) {
              setPublishingData(initialPublishingData);
            }
          })
        );
      } else {
        dispatch(
          createContractAction(payload, setLoading, dispatch, (res) => {
            if (res.success) {
              // Reset the form fields after successful submission
              setPublishingData(initialPublishingData);
            }
          })
        );
      }
    }
  };

  const onMusicVideoSubmit = async () => {
    const hasFormErrors = showFormError(musicVideoData, setMusicVideoData);
    if (!hasFormErrors) {
      const payload = {
        agreementName: musicVideoData.agreementName,
        primaryArtist: {
          fullLegalName: musicVideoData.fullLegalName,
          name: musicVideoData.name,
          address: musicVideoData.address,
        },
        details: {
          director: musicVideoData.director,
          bussinessAddress: musicVideoData.bussinessAddress,
          realeaseTitle: musicVideoData.realeaseTitle,
          scopeProduction: musicVideoData.scopeProduction,
          musicVideoFee: musicVideoData.musicVideoFee,
          paymentSchedule: musicVideoData.paymentSchedule,
          paymentTerms: musicVideoData.paymentTerms,
          videoFormat: musicVideoData.videoFormat,
          deliveryDate: musicVideoData.deliveryDate,
          days: musicVideoData.days,
          jurisdiction: musicVideoData.jurisdiction,
          disputeSettlement: musicVideoData.disputeSettlement,
          termination: musicVideoData.termination,
          governmentlaw: musicVideoData.governmentlaw,
          producerQuotation: musicVideoData.producerQuotation,
        },
        clauses: musicVideoData.clauses,
        agreementType: data.agreementType,
        agreementId: data.agreementId,
        agreementDate: handleFormatDate(new Date()),
        project: data.project,
      };
      if (id) {
        dispatch(
          updateContractAction(id, payload, setLoading, (res) => {
            if (res?.success) {
              setMusicVideoData(initialMusicVideoData);
            }
          })
        );
      } else {
        dispatch(
          createContractAction(payload, setLoading, dispatch, (res) => {
            if (res.success) {
              setMusicVideoData(initialMusicVideoData);
            }
          })
        );
      }
    }
  };

  const onArtworkSubmit = async () => {
    const hasFormErrors = showFormError(artworkData, setArtworkData);
    if (!hasFormErrors) {
    const payload = {
      agreementName: artworkData.agreementName,
      primaryArtist: {
        fullLegalName: artworkData.fullLegalName,
        name: artworkData.name,
        address: artworkData.address,
      },
      details: {
        artworkCreatorName: artworkData.artworkCreatorName,
        artworkCreatorAddress: artworkData.artworkCreatorAddress,
        musicRelaseName: artworkData.musicRelaseName,
        artWorkDescription: artworkData.artWorkDescription,
        artworkFee: `${artworkData.artworkFee} ${artworkData.currencySymbol.symbol}`,
        symbol: artworkData.currencySymbol,
        paymentStructure: artworkData.paymentStructure,
        deliveryFormat: artworkData.deliveryFormat,
        deliveryDate: artworkData.deliveryDate,
        revisonRound: artworkData.revisonRound,
        additionalCharges: `${artworkData.additionalCharges} ${artworkData.currencySymbol.symbol}`,
        days: artworkData.days,
        law: artworkData.law,
        warrenties: artworkData.warrenties,
      },
      clauses: artworkData.clauses,
      agreementId: data.agreementId,
      agreementType: data.agreementType,
      agreementDate: handleFormatDate(new Date()),
      project: data.project,
    };
    console.log(payload, "payload");
    if (id) {
      dispatch(
        updateContractAction(id, payload, setLoading, (res) => {
          if (res?.success) {
            setArtworkData(initialArtworkData);
            navigate("/contracts");
          }
        })
      );
    } else {
      dispatch(
        createContractAction(payload, setLoading, dispatch, (res) => {
          if (res.success) {
            // Reset the form fields after successful submission
            setArtworkData(initialArtworkData);
            navigate("/contracts");
          }
        })
      );
    }
    }
  };

  const onRemixSubmit = async () => {
    const hasFormErrors = showFormError(remixData, setRemixData);

    if (!hasFormErrors) {
      const payload = {
        agreementName: remixData.agreementName,
        primaryArtist: {
          fullLegalName: remixData.fullLegalName,
          name: remixData.name,
          address: remixData.address,
        },
        details: {
          remixerFullLegalName: remixData.remixerFullLegalName,
          remixerName: remixData.remixerName,
          remixerAddress: remixData.remixerAddress,
          releaseName: remixData.releaseName,
          editNumber: remixData.editNumber,
          bearingTheCost: remixData.bearingTheCost,
          remixAudioFormat: remixData.remixAudioFormat,
          remixFee: remixData.remixFee,
          splitPercentage: remixData.splitPercentage,
          remixCredit: remixData.remixCredit,
          deadline: remixData.deadline,
          notice: remixData.email,
          remedies: remixData.law,
        },
        clauses: remixData.clauses,
        agreementType: data.agreementType,
        agreementId: data.agreementId,
        agreementDate: handleFormatDate(new Date()),
        project: data.project,
      };

      if (id) {
        dispatch(
          updateContractAction(id, payload, setLoading, (res) => {
            if (res?.success) {
              setRemixData(initialRemixData);
            }
          })
        );
      } else {
        dispatch(
          createContractAction(payload, setLoading, dispatch, (res) => {
            if (res.success) {
              // Reset the form fields after successful submission
              setRemixData(initialRemixData);
            }
          })
        );
      }
    }
  };

  const onSignSubmit = (role) => {
    setCurrentSigner(role);
    setSignatureModalVisible(true);
  };

 const onDocumentSubmit = async () => {
  try {
    setLoading(true);
    dispatch(
      GetContractDetailsActon(id, async (data) => {
        const status = data?.payload?.status;
        const content = { htmlContent };

        if (status === "IN_PROGRESS") {
          await dispatch(updateDocumentAction(id, content, setLoading));
          setLoading(false);
          // Refetch contract details after update
          dispatch(GetContractDetailsActon(id, (updatedData) => {
            setContractStatus(updatedData?.payload?.status);
          }));
          return;
        } else {
          const sendingData = Object.entries(sign).reduce(
            (acc, [role, { signature, typedSignature }]) => {
              acc[role] = { role, signature, typedSignature };
              return acc;
            },
            {}
          );
          const firstRoleKey = Object.keys(sendingData)[0];
          const payload = sendingData[firstRoleKey];
          const hasSignature = payload?.signature || payload?.typedSignature;

          if (!hasSignature) {
            dispatch(
              showToastAction({
                type: "error",
                detail: "Document needs to be signed first",
              })
            );
            setLoading(false);
            return;
          }
          await dispatch(signContractAction(id, payload, setLoading, dispatch));
          // Refetch contract details after signing
          dispatch(GetContractDetailsActon(id, (updatedData) => {
            setContractStatus(updatedData?.payload?.status);
          }));
        }
        setLoading(false);
      })
    );
  } catch (error) {
    console.error("Error in document submission:", error);
    setLoading(false);
  }
};

  const handleSaveSignature = (signatureData) => {
    if (!currentSigner) return;
    setSign((prev) => ({
      ...prev,
      [currentSigner]: {
        signature:
          signatureData.signature ?? prev[currentSigner]?.signature ?? "",
        typedSignature:
          signatureData.typedSignature ??
          prev[currentSigner]?.typedSignature ??
          "",
      },
    }));

    setSignData(signatureData);
    setSignatureModalVisible(false);
    setCurrentSigner(null);
  };

  const handleClear = (role) => {
    setSign((prev) => ({
      ...prev,
      [role]: {
        signature: "",
        typedSignature: "",
      },
    }));
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }]["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
  ];

  return {
    sign,
    setSign,
    data,
    setData,
    producerData,
    setProducerData,
    sideArtistData,
    setSideArtistData,
    publishingData,
    setPublishingData,
    musicVideoData,
    setMusicVideoData,
    artworkData,
    setArtworkData,
    remixData,
    setRemixData,
    handleChange,
    activeIndex,
    setActiveIndex,
    onTermsSubmit,
    onDocumentSubmit,
    onSignSubmit,
    projectsList,
    profile,
    loading,
    signatureModalVisible,
    setSignatureModalVisible,
    typedSignature,
    setTypedSignature,
    drawnSignature,
    setDrawnSignature,
    handleSaveSignature,
    contractData,
    setContractData,
    shareDialog,
    setShareDialog,
    emails,
    setEmails,
    showAdditionalEmail,
    setShowAdditionalEmail,
    addEmailField,
    removeEmailField,
    handleEmailChange,
    handleClear,
    currentSigner,
    setCurrentSigner,
    handleShareContract,
    shareLoading,
    setShareLoading,
    agreements,
    setAgreements,
    modules,
    htmlContent,
    setHtmlContent,
    modules,
    quillRef,
    contractStatus,
    onSideArtistSubmit,
    onArtworkSubmit
  };
};

export default ContractContainer;
